import type {ReactElement, ReactNode} from 'react';
import React, {useMemo, useRef, useState} from 'react';
import type {ChatMessage, ChatMessagePart, FailedMessage, ImagePart, ModelMessage, UserMessage} from '@Components/ai-chat-window/ai-chat-window.types';
import {ChatMessagePartType, FailedMessageType, ImageGenerationContextMode, MessageRole} from '@Components/ai-chat-window/ai-chat-window.types';
import {UserMessageRow} from '@Components/ai-chat-window/components/user-message-row/user-message-row';
import {AssistantMessageRow} from '@Components/ai-chat-window/components/assistant-message-row/assistant-message-row';
import {FailedMessageRow} from '@Components/ai-chat-window/components/failed-message-row/failed-message-row';
import {groupMessagesIntoConversations} from '@Components/ai-chat-window/lib/ai-chat-messages.library';
import styles from './chat-messages.module.scss';
import type {ChatMessagesDisplayProps, LightboxImageEntry} from '@Components/ai-chat-window/components/chat-messages-display/chat-messages.types';
import {findLightboxImageIndex} from '@Components/ai-chat-window/components/chat-messages-display/chat-messages.library';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {getUniqueKeyForImagePart} from '@Libraries/ai-chat.library';
import {ImagePartsLightbox} from '@Components/ai-chat-window/components/image-parts-lightbox/image-parts-lightbox';
import {AiThreeDotsLoader} from '@Components/ai-chat-window/components/ai-three-dots-loader/ai-three-dots-loader';
import {useSelector} from 'react-redux';
import type {RootState} from '@/store';
import {scrollToBottom} from '@Utils/dom.util';
import {AnimatePresence, motion} from 'framer-motion';
import {isImagePart} from '@Components/ai-chat-window/components/user-message-row/components/user-attachment/user-attachment.types';

const TOP_OFFSET = 52;
const TOP_OFFSET_SMALL = 0;

function ChatMessages({
  isMessageStreaming,
  messages,
  chatId,
  bubbleClassNames,
  onRetry,
  onMessageRefMount,
  onRegenerate,
  onGenerate,
  onReattach,
  onEditSend,
  editSendInProgressMessageKey,
  waitingForRefreshedChatToFinish = false,
  ...props
}: ChatMessagesDisplayProps): ReactNode {
  const {state} = useAiChat();
  const {isSmall, isSuperUser, showDeletedMessages} = state;
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const displayedMessages = useMemo((): Record<string, ChatMessage> => {
    if (!isSuperUser || !showDeletedMessages) {
      return Object.fromEntries(Object.entries(messages).filter(([, msg]) => !msg.deletedAt));
    }
    return messages;
  }, [messages, isSuperUser, showDeletedMessages]);
  const conversations = groupMessagesIntoConversations(displayedMessages);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState<number | null>(null);

  const {hasWriteAccess} = useSelector((state: RootState) => {
    if (!chatId) {
      return {hasWriteAccess: true};
    }

    const chatState = state.aiChatWindow[chatId];
    return {hasWriteAccess: chatState?.hasWriteAccess ?? true};
  });

  const firstImageGenerationMessageId = useMemo((): string | undefined => {
    return Object.values(displayedMessages).find((msg) => msg.parts?.some((p) => p.type === ChatMessagePartType.MODEL_GENERATED_IMAGE))?.messageId;
  }, [displayedMessages]);

  const latestCreateMessageId = useMemo((): string | undefined => {
    return Object.values(displayedMessages)
      .reverse()
      .find((msg) => msg.role === MessageRole.ASSISTANT && msg.mode === ImageGenerationContextMode.CREATE && msg.parts?.some((p) => p.type === ChatMessagePartType.MODEL_GENERATED_IMAGE))?.messageId;
  }, [displayedMessages]);

  const templatesTileMessageId = latestCreateMessageId ?? firstImageGenerationMessageId;

  const allImageEntries = useMemo((): LightboxImageEntry[] => {
    const entries: LightboxImageEntry[] = [];

    Object.values(displayedMessages).forEach((message: ChatMessage) => {
      message.parts?.forEach((part: ChatMessagePart) => {
        if (isImagePart(part)) {
          entries.push({part, messageId: message.messageId, uniqueKey: getUniqueKeyForImagePart(part)});
        }
      });
    });

    return entries;
  }, [displayedMessages]);

  const allImageParts = useMemo((): ImagePart[] => allImageEntries.map((entry) => entry.part), [allImageEntries]);

  if (!chatId) {
    return null;
  }

  const getTopOffsetForLastConversation = (): number => {
    return isSmall ? TOP_OFFSET_SMALL : TOP_OFFSET;
  };

  const handleRef = (container: HTMLDivElement | null): void => {
    if (messagesRef.current) {
      return;
    }

    messagesRef.current = container;

    if (container) {
      onMessageRefMount(container);

      const lastConversationIndex = conversations.length - 1;

      const lastConvElement = container.querySelector<HTMLElement>(`div[data-conversation="${lastConversationIndex}"]`);

      if (lastConvElement) {
        container.scrollTo({
          top: lastConvElement.offsetTop - getTopOffsetForLastConversation(),
          behavior: 'instant',
        });
      } else {
        scrollToBottom(container, 'instant');
      }
    }
  };

  const getThreeDotsLoader = (): ReactElement | null => {
    if (!waitingForRefreshedChatToFinish) {
      return null;
    }

    return (
      <div className={styles.singleMessageContainer}>
        <AiThreeDotsLoader />
      </div>
    );
  };

  const getChatMessage = (message: ChatMessage, messageKey: string, isLastMessage: boolean, isLastConversation: boolean): ReactElement => {
    const showDeletedBorder = isSuperUser && showDeletedMessages && message.deletedAt;
    const content = getChatMessageForRole(message, messageKey, isLastMessage, isLastConversation);
    return (
      <div
        style={{
          paddingBottom: `${message.role !== MessageRole.USER && isLastMessage && props.heightAdjustmentForFloatingPromptBox ? props.heightAdjustmentForFloatingPromptBox + (isSmall ? 80 : 160) : 0}px`,
        }}
        key={messageKey}
        className={styles.singleMessageContainer}
        data-message-id={message.messageId}
        data-role={message.role}
      >
        {showDeletedBorder ? <div className={styles.deletedMessage}>{content}</div> : content}
      </div>
    );
  };

  const getUserMessage = (message: UserMessage, messageKey: string): ReactElement => {
    return (
      <UserMessageRow
        messageKey={messageKey}
        onImageClick={handleOpenLightbox}
        onReattach={onReattach}
        onEditSend={onEditSend}
        editSendInProgress={editSendInProgressMessageKey === message.messageId}
        isMobile={isSmall}
        message={message}
        bubbleClassNames={bubbleClassNames}
        chatId={chatId}
        isMessageStreaming={isMessageStreaming}
      />
    );
  };

  const getAssistantMessage = (message: ModelMessage, isLastMessage: boolean, isLastConversation: boolean): ReactElement => {
    const showLoader = isLastMessage && isLastConversation && isMessageStreaming;

    return (
      <AssistantMessageRow
        isMessageStreaming={isMessageStreaming}
        onImageClick={handleOpenLightbox}
        onReattach={onReattach}
        message={message}
        chatId={chatId}
        bubbleClassNames={bubbleClassNames}
        onRegenerate={onRegenerate}
        onGenerate={onGenerate}
        isLastMessage={isLastMessage}
        showLoader={showLoader}
        isMobile={isSmall}
        messages={messages}
        isFirstImageGeneration={firstImageGenerationMessageId === message.messageId}
        isLatestCreateMessage={templatesTileMessageId === message.messageId}
        onResumeWorkflow={props.onResumeWorkflow}
      />
    );
  };

  const getFailedMessage = (message: FailedMessage, isLastMessage: boolean): ReactElement | null => {
    const shouldShowRetry = (): boolean => {
      return hasWriteAccess && isLastMessage && message.failureType !== FailedMessageType.USER_IMAGE_PROCESSING_ERROR && message.failureType !== FailedMessageType.DAILY_LIMIT_REACHED && message.failureType !== FailedMessageType.CONTENT_POLICY_VIOLATION && message.failureType !== FailedMessageType.UNSUPPORTED_LANGUAGE;
    };

    return <FailedMessageRow message={message} onRetry={onRetry} showRetry={shouldShowRetry()} />;
  };

  const getChatMessageForRole = (message: ChatMessage, messageKey: string, isLastMessage: boolean, isLastConversation: boolean): ReactElement | null => {
    switch (message.role) {
      case MessageRole.USER:
        return getUserMessage(message, messageKey);
      case MessageRole.ASSISTANT:
        return getAssistantMessage(message, isLastMessage, isLastConversation);
      case MessageRole.DEVELOPER:
        if ('failureType' in message) {
          return getFailedMessage(message, isLastMessage);
        }

        return null;
      default:
        return null;
    }
  };

  const handleCloseLightbox = (): void => {
    setIsLightboxOpen(false);
  };

  const handleOpenLightbox = (messageId: string | undefined, uniqueKey: string): void => {
    const imageIndex = findLightboxImageIndex(allImageEntries, messageId, uniqueKey);

    if (imageIndex !== -1) {
      setCurrentImageIndex(imageIndex);
      setIsLightboxOpen(true);
    }
  };

  const handlePreviousImage = (): void => {
    if (currentImageIndex !== null && currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const handleNextImage = (): void => {
    if (currentImageIndex !== null && currentImageIndex < allImageParts.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const messageGutterSpacing = isSmall ? 'gap-5' : 'gap-9';

  return (
    <div
      id="pmw-chat-messages-container"
      ref={handleRef}
      className={`${isSmall ? 'spacing-p-l-4 spacing-p-r-4' : ''} flex-v-row ${messageGutterSpacing} ${styles.messages}`}
      role="log"
      aria-live="polite"
    >
      <AnimatePresence mode="popLayout">
        {conversations.map((conversation, convIndex) => {
          const isLastConversation = convIndex === conversations.length - 1;

          return (
            <motion.div
              key={`conversation-${conversation[0]?.messageId ?? convIndex}`}
              data-conversation={convIndex}
              className={`flex-v-row _full-width ${styles.conversation} ${isLastConversation ? styles.lastConversation : ''} ${isSmall ? styles.small : ''}`}
              initial={false}
              exit={{opacity: 0, transition: {duration: 0.3}}}
            >
              {conversation.map((message, msgIndex) => {
                const isLastMessage = isLastConversation && msgIndex === conversation.length - 1;
                const messageKey = message.messageId ?? `message-${message.role}-${convIndex}-${msgIndex}`;
                return getChatMessage(message, messageKey, isLastMessage, isLastConversation);
              })}

              {isLastConversation ? getThreeDotsLoader() : null}
            </motion.div>
          );
        })}
      </AnimatePresence>

      <ImagePartsLightbox
        canWrite={hasWriteAccess}
        chatId={chatId}
        imageParts={allImageParts}
        isLightboxOpen={isLightboxOpen}
        currentImageIndex={currentImageIndex}
        handleCloseLightbox={handleCloseLightbox}
        handlePreviousImage={handlePreviousImage}
        handleNextImage={handleNextImage}
        messages={messages}
        onReattach={onReattach}
      />
    </div>
  );
}

export default React.memo(ChatMessages);
